<template>
  <v-app>
    <v-app-bar app color="#262D46" dark fixed height="48">
      <v-row class="flex-nowrap no-gutters">
        <v-col />
        <v-col class="d-flex flex-row align-center justify-center">
          <v-toolbar-title data-ktznta="121" v-text="title" />
        </v-col>
        <v-col class="d-flex flex-row justify-end">
          <v-btn v-if="isChatList && isCustomerService" dark icon width="auto" @click="openRespondersDialog()">
            {{ bizStaffResponder.name }}
            <v-icon>mdi-account</v-icon>
          </v-btn>
          <v-btn v-if="isChatList && !isCustomerService" dark icon @click="openProfile()">
            <v-icon>mdi-qrcode</v-icon>
          </v-btn>
          <SubscReConnectComponent v-if="isChatList && isCustomerService" />
          <NoticeUnreadComponent />
          <ConnectivityIndicatorComponent />
        </v-col>
      </v-row>
    </v-app-bar>

    <v-main>
      <router-view />
    </v-main>

    <v-bottom-navigation v-model="bottomNav" app fixed grow>
      <v-btn v-if="isCustomerService" :to="loginStatus" data-ktznta="107" height="inherit" value="loginStatus">
        <span>{{ $t("lbl_status") }}</span>
        <v-icon>toggle_off</v-icon>
      </v-btn>

      <v-btn :to="conversations" data-ktznta="108" height="inherit" value="chat">
        <span>{{ $t("navBarChat") }}</span>
        <v-icon>mdi-message-text</v-icon>
      </v-btn>

      <v-btn :to="translation" data-ktznta="109" height="inherit" value="translation">
        <span>{{ $t("translation") }}</span>
        <v-icon>mdi-translate</v-icon>
      </v-btn>

      <v-btn :to="settings" data-ktznta="110" height="inherit" value="settings">
        <span>{{ $t("navBarSettings") }}</span>
        <v-icon>settings</v-icon>
      </v-btn>
    </v-bottom-navigation>

    <v-dialog v-model="profileDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <Profile
        :icon="profileDialogData.icon"
        :name="profileDialogData.name"
        :qr="profileDialogData.qr"
        :qrUrl="profileDialogData.qrUrl"
        :title="profileDialogData.title"
        @close="profileDialog = false"
      />
    </v-dialog>

    <!-- should replace with responderCard? -->
    <v-dialog v-model="responders_dialog.opend" max-width="290" persistent scrollable>
      <v-card>
        <v-card-title class="justify-center">{{ $t("select_responder") }}</v-card-title>
        <v-card-text style="overflow-x: hidden">
          <v-row class="pb-6" style="text-align: center">
            <v-col v-for="(responder, idx) in responders" :key="idx">
              <v-btn
                v-if="responder.id === bizStaffResponder.id"
                color="#fc461c"
                outlined
                width="240"
                @click="selectResponder(responder)"
                >{{ truncateName(responder.name) }}
              </v-btn>
              <v-btn
                v-if="responder.id !== bizStaffResponder.id"
                class="responder_not_selected"
                color="#fc461c"
                text
                width="240"
                @click="selectResponder(responder)"
                >{{ truncateName(responder.name) }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- dialog to ask the user to change their "status" (what inquiries they get notify about) settings -->
    <v-dialog v-if="!responders_dialog.opend" v-model="statusConfirm.opend" persistent width="400">
      <v-card data-ktznta="104" style="overflow: hidden">
        <v-card-title
          style="word-break: break-word; white-space: pre-line"
          v-html="$t('inquiry_status_setting_reminder')"
        />
        <v-row class="pb-3 pt-3">
          <v-spacer />
          <v-col>
            <v-btn
              :to="loginStatus"
              class="white--text"
              color="#fc461c"
              data-ktznta="105"
              @click="statusConfirm.opend = false"
            >
              {{ $t("common_yes") }}
            </v-btn>
          </v-col>
          <v-col>
            <v-btn class="white--text" color="#fc461c" data-ktznta="106" @click="statusConfirm.opend = false">
              {{ $t("cancel") }}
            </v-btn>
          </v-col>
          <v-spacer />
        </v-row>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script lang="ts">
import store from "@/store";
import { computed, createComponent, onMounted, value, Wrapper } from "vue-function-api";
import { truncateText } from "@/framework/utilities/truncateText";
import { getBizResponder } from "@/modules/staff/services/responderApi";
import { i18n } from "../plugins/i18n";
import { ComponentMutation } from "@/framework/store/component/types";
import { MembersEditMode } from "@/modules/staff/objects/group/MembersEditMode";
import { QcBizResponder } from "@/modules/staff/objects/QcBizResponder";
import { ErrorService } from "@/framework/errors/ErrorService";
import { StaffService } from "../api/Staff/StaffService";
import { LoginStatusApiService } from "@/framework/api/Staff/LoginStatusApiService";
import { InquiryService } from "@/framework/api/Staff/ChatService/InquiryService";
import ConnectivityIndicatorComponent from "../components/ConnectivityIndicator.vue";
import SubscReConnectComponent from "../components/SubscReConnect.vue";
import NoticeUnreadComponent from "../components/NoticeUnread.vue";
import Profile from "../components/Profile.vue";
import CustomError from "@/framework/errors/CustomError";
import { UserService } from "@/modules/auth/services/UserService";
import { QueryBizResponder, StaffLoginStatus } from "@/modules/staff/objects/Staff";

export default createComponent({
  components: {
    Profile,
    NoticeUnreadComponent,
    SubscReConnectComponent,
    ConnectivityIndicatorComponent,
  },
  setup(props, context) {
    const $router = context.root.$router;
    const $store: any = context.root.$store;
    const inquiryService = new InquiryService();
    const errorService = new ErrorService();
    const userService = new UserService();
    const title: Wrapper<string> = value(`${i18n.t("navBarChat")}`);
    const bottomNav: Wrapper<string> = value("recent");
    const isChatList: Wrapper<boolean> = value(false);
    const profileDialog: Wrapper<boolean> = value(false);
    const responders: Wrapper<QcBizResponder[]> = value([]);
    const profileDialogData: Wrapper<any> = value({});
    const bizStaffResponder: Wrapper<QueryBizResponder> = value({});
    const responders_dialog: Wrapper<any> = value({ opend: false });
    const statusConfirm: Wrapper<any> = value({ opend: false });

    // Computed Properties
    const conversations = computed(() => `/${i18n.locale}/conversations`);
    const travelchat = computed(() => `/${i18n.locale}/travelchat`);
    const translation = computed(() => `/${i18n.locale}/translation`);
    const settings = computed(() => `/${i18n.locale}/settings`);
    const loginStatus = computed(() => `/${i18n.locale}/biz/staff/login/status`);
    const subscriptionEventTest = computed(() => `/${i18n.locale}/subscription/test`);
    const isCustomerService = computed(() => {
      const appMode = $store.getters.appMode;
      // Check if the setAppMode action exists before committing to vuex store to avoid error
      const methodName = "setAppMode";
      const actionExists = Object.keys($store._actions).findIndex((key) => key === methodName) !== -1;
      if (actionExists) {
        if (appMode && appMode == "group") {
          return false;
        } else if (appMode && appMode == "customer_service") {
          return true;
        } else {
          if (!!$store.getters.user && $store.getters.user.userType === "biz_staff") {
            $store.commit(ComponentMutation.SET_APP_MODE, "customer_service");
            return true;
          }
          $store.commit(ComponentMutation.SET_APP_MODE, "group");
          return false;
        }
      }
    });

    onMounted(async () => {
      if (store.getters.user && store.getters.user.userType === "biz_staff") {
        await new StaffService().fetchResponders().then((listRespondersApiRes) => {
          if (listRespondersApiRes.isErr()) {
            return errorService.handleError(listRespondersApiRes.error);
          }
          responders.value = listRespondersApiRes.value;
        });

        const res = await getBizResponder();
        if (res.isErr()) {
          return await errorService.handleError(res.error);
        }
        bizStaffResponder.value = res.value;

        if ($store.getters.isFromLogin && responders.value.length > 0) {
          if (responders.value.length == 1) {
            await selectResponder(responders.value[0]);
          } else {
            responders_dialog.value.opend = true;
          }
        }

        // probably need a nicer way to do this, but for now, we have to re-get the staffLoginStatus
        if (
          store.getters &&
          store.getters.staff &&
          store.getters.staff.myself &&
          store.getters.staff.myself.role &&
          ["staff", "admin"].includes(store.getters.staff.myself.role) &&
          !$store.getters.isRedirected
        ) {
          const res = await new LoginStatusApiService().getLoginStatus();
          if (res.isErr()) {
            return await errorService.handleError(res.error);
          }

          const staffLoginStatus = (res.value && res.value[0]) as StaffLoginStatus;
          if (staffLoginStatus) {
            let loginCount = staffLoginStatus.default_groups.filter((x) => x.is_login).length;
            loginCount += staffLoginStatus.non_default_groups.filter((x) => x.is_login).length;
            if (loginCount < 1) {
              statusConfirm.value.opend = true;
            }
          } else new CustomError().setErrorMessageControls("", "", "No data from BE", "");
        }
      }
      $store.commit(ComponentMutation.SET_IS_REDIRECTED, false);
      $store.commit(ComponentMutation.SET_IS_FROM_LOGIN, false);
      console.log("==========Default onMounted==========");
    });

    store.subscribe((mutation, state) => {
      if (mutation.type !== ComponentMutation.SET_NAVIGATION) return;
      title.value = mutation.payload.title;
      isChatList.value = mutation.payload.isChatList;
    });

    const addChat = () => {
      store.dispatch("clearGroupProfile");
      $router.push({
        path: `/${i18n.locale}/group/members/edit/new`,
        query: { mode: MembersEditMode.New.toString() },
      });
    };

    const openProfile = async () => {
      let title, qr, qrUrl;
      const user = await userService.getCachedQcUser();
      if (user.isErr()) {
        console.error("Failed to get user info");
        return;
      }

      qr = user.value.qr;
      qrUrl = `${process.env.VUE_APP_KTZN_PROXY}${user.value.id}`;
      title = i18n.t("my_qr").toString();
      profileDialogData.value = {
        title: title,
        name: user.value.username,
        icon: user.value.icon || require("../assets/user-icon.svg"),
        qr: qr,
        qrUrl: qrUrl,
      };
      profileDialog.value = true;
    };

    const selectResponder = async (responder: QcBizResponder) => {
      if (responder && responder.id) {
        const setLoggedInResponderRes = await inquiryService.setResponder(responder.id);
        if (setLoggedInResponderRes.isErr()) {
          return await errorService.handleError(setLoggedInResponderRes.error);
        }
      }
      bizStaffResponder.value = responder;
      responders_dialog.value.opend = false;
    };

    const openRespondersDialog = () => {
      if (responders.value.length > 0) {
        responders_dialog.value.opend = true;
      }
    };

    const truncateName = (name: string) => {
      return truncateText(name, 13, 20);
    };

    return {
      title,
      bottomNav,
      conversations,
      travelchat,
      translation,
      settings,
      isChatList,
      profileDialog,
      profileDialogData,
      loginStatus,
      isCustomerService,
      subscriptionEventTest,
      responders,
      responders_dialog,
      statusConfirm,
      bizStaffResponder,
      addChat,
      openProfile,
      selectResponder,
      openRespondersDialog,
      truncateName,
    };
  },
});
</script>
<style>
.responder_not_selected {
  background-color: #fef5f3;
}

.v-card__text {
  overflow-x: hidden;
}
</style>
